import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import { type FormEvent, useId, useRef, useState } from "react";
import AssignOrganizationForm from "@/components/admin/collections/AssignOrganizationForm";
import UnassignConfirmModal from "@/components/admin/collections/UnassignConfirmModal";
import { GfgButton } from "@/components/ui/GfgButton";
import {
  ASSIGN_MODAL_TITLE,
  ASSIGN_SUCCESS_TOAST,
  CANCEL_LABEL,
  CLOSE_LABEL,
  SAVE_FALLBACK,
  SAVE_LABEL,
  UNASSIGN_FALLBACK,
  UNASSIGN_SUCCESS_TOAST,
} from "@/constants/adminCollections";
import { useAssignCustomProduct, useUnassignCustomProduct } from "@/hooks/useAdminCollections";
import type {
  AdminCollectionProduct,
  AssignFormValue,
  OrganizationOption,
} from "@/interfaces/adminCollections";
import { initialAssignForm, resolveAssignSubmit } from "@/utils/adminCollections-helper";
import { apiErrorMessage } from "@/utils/apiError-helper";

interface AssignOrganizationModalProps {
  /** The page mounts this only while a product is being edited, so the prefill is read once. */
  product: AdminCollectionProduct;
  organizationOptions: readonly OrganizationOption[];
  priceTiers: readonly number[];
  onClose: () => void;
  /** Reports the success message so the page can toast it after this dialog unmounts. */
  onSuccess: (message: string) => void;
  /** Reports a failure message to the page; the dialog also keeps it inline so the user can retry. */
  onError: (message: string) => void;
}

export default function AssignOrganizationModal({
  product,
  organizationOptions,
  priceTiers,
  onClose,
  onSuccess,
  onError,
}: AssignOrganizationModalProps) {
  const titleId = useId();
  const current = product.assignment;
  const [value, setValue] = useState<AssignFormValue>(() => initialAssignForm(product));
  const [error, setError] = useState<string | null>(null);
  const [confirmingUnassign, setConfirmingUnassign] = useState(false);
  const assign = useAssignCustomProduct();
  const unassign = useUnassignCustomProduct();
  // Blocks a same-tick double submit: `isPending` only flips after the next render.
  const inFlight = useRef(false);
  const busy = assign.isPending || unassign.isPending;

  function handleChange(next: AssignFormValue) {
    setValue(next);
    setError(null);
  }

  function reportError(message: string) {
    setError(message);
    onError(message);
  }

  async function handleSave() {
    const submit = resolveAssignSubmit(current, value);
    if (submit.kind === "invalid") return setError(submit.error);
    if (submit.kind === "noop") return onClose();
    if (submit.kind === "unassign") return setConfirmingUnassign(true);
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await assign.mutateAsync({
        productId: product.id,
        body: { organizationId: submit.organizationId, priceTier: submit.priceTier },
      });
    } catch (e) {
      return reportError(apiErrorMessage(e, SAVE_FALLBACK));
    } finally {
      inFlight.current = false;
    }
    onSuccess(ASSIGN_SUCCESS_TOAST);
    onClose();
  }

  async function handleConfirmUnassign() {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await unassign.mutateAsync(product.id);
    } catch (e) {
      setConfirmingUnassign(false);
      return reportError(apiErrorMessage(e, UNASSIGN_FALLBACK));
    } finally {
      inFlight.current = false;
    }
    onSuccess(UNASSIGN_SUCCESS_TOAST);
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    void handleSave();
  }

  return (
    <>
      <Dialog
        open
        aria-labelledby={titleId}
        onClose={busy ? undefined : onClose}
        disableScrollLock
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              p: "32px",
              m: 2,
              width: 480,
              maxWidth: "calc(100vw - 32px)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            },
          },
        }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-5 flex items-center justify-between">
            <h2 id={titleId} className="font-crimson text-2xl text-rodeo-black">
              {ASSIGN_MODAL_TITLE}
            </h2>
            <button
              type="button"
              aria-label={CLOSE_LABEL}
              disabled={busy}
              onClick={onClose}
              className="cursor-pointer border-none bg-transparent text-graphite disabled:cursor-default disabled:opacity-40"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <AssignOrganizationForm
            product={product}
            organizationOptions={organizationOptions}
            priceTiers={priceTiers}
            value={value}
            onChange={handleChange}
            error={error}
            currentOrganizationName={current?.organizationName}
          />

          <div className="mt-6 flex items-center justify-end gap-3">
            <GfgButton type="button" variant="secondary" disabled={busy} onClick={onClose}>
              {CANCEL_LABEL}
            </GfgButton>
            <GfgButton type="submit" variant="primary" disabled={busy}>
              {SAVE_LABEL}
            </GfgButton>
          </div>
        </form>
      </Dialog>

      {confirmingUnassign && current && (
        <UnassignConfirmModal
          productName={product.name}
          organizationName={current.organizationName}
          onClose={() => setConfirmingUnassign(false)}
          onConfirm={() => void handleConfirmUnassign()}
          pending={unassign.isPending}
        />
      )}
    </>
  );
}
